/**
 * Where a plan says the work belongs, at its gate (ENG-2966, ENG-3033), as the
 * one-attempt lane's plan gate writes it (`openPlanGateActivity`):
 *
 * - `payload.move`: the change belongs in another of the Team's repositories.
 *   "This looks like it belongs in X. Move it?" with one click that starts
 *   the ticket again there (Start over, with `moveTo`).
 * - `payload.needs`: a repository the change needs a change in that the Team
 *   has not attached. "Needs X: attach it to the Team or split."
 */
import { useState } from "react";
import type { WorkItemGate } from "@yagni/shared";

export interface PlanMoveOffer {
  repoId: string;
  fullName: string;
  why: string;
  line: string;
}

export interface PlanNeed {
  repository: string;
  what: string;
  line: string;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** The gate's move offer, or null for a gate that makes none. */
export function readPlanMove(gate: WorkItemGate | null | undefined): PlanMoveOffer | null {
  const raw = gate?.payload?.move;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const move = raw as Record<string, unknown>;
  if (typeof move.repoId !== "string" || typeof move.fullName !== "string") return null;
  return {
    repoId: move.repoId,
    fullName: move.fullName,
    why: text(move.why),
    line: text(move.line) || `This looks like it belongs in ${move.fullName}. Move it?`,
  };
}

/** The repositories the gate says the change needs and the Team lacks. */
export function readPlanNeeds(gate: WorkItemGate | null | undefined): PlanNeed[] {
  const raw = gate?.payload?.needs;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const need = entry as Record<string, unknown> | null;
    if (!need || typeof need.repository !== "string") return [];
    return [
      {
        repository: need.repository,
        what: text(need.what),
        line: text(need.line) || `Needs ${need.repository}: attach it to the Team or split.`,
      },
    ];
  });
}

export interface PlanRepositoryLinesProps {
  gate: WorkItemGate | null | undefined;
  canMove: boolean;
  onMove: (move: PlanMoveOffer) => Promise<void>;
}

export function PlanRepositoryLines({ gate, canMove, onMove }: PlanRepositoryLinesProps) {
  const move = readPlanMove(gate);
  const needs = readPlanNeeds(gate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!move && needs.length === 0) return null;
  return (
    <>
      {move && (
        <div className="blk" data-testid="plan-move-offer">
          <div className="bh">
            <span className="label">{move.line}</span>
          </div>
          {move.why && <p className="body-p">{move.why}</p>}
          {canMove && (
            <div className="tri-actions">
              <button
                type="button"
                className="is-quiet"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  onMove(move)
                    .catch((err: unknown) =>
                      setError(err instanceof Error ? err.message : "That did not go through."),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? "Moving" : `Move to ${move.fullName} and plan again`}
              </button>
            </div>
          )}
          {error && (
            <span className="fact" role="alert">
              {error}
            </span>
          )}
        </div>
      )}
      {needs.length > 0 && (
        <div className="blk" data-testid="plan-needs">
          <div className="bh">
            <span className="label">Needs other repositories</span>
          </div>
          <ul className="md-body brief">
            {needs.map((need) => (
              <li key={need.repository}>{need.line}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
